import { createAsyncThunk } from "@reduxjs/toolkit";
import { iStateMessage } from "@/customTypes/GenericReduxTypes";
import { iGenericResponse } from "@/customTypes/CommonServiceTypes";
import { curryGetThunkName } from "@/projectUtils/ReduxUtil";
import { httpStatusCodes } from "@/customTypes/NetworkTypes";
import { crmSetup } from "@/services/api/cresivoxServer";
import { REDUCER_NAME } from "./Types";

const curriedGetThunkName = curryGetThunkName(REDUCER_NAME);

type R = iGenericResponse<any | null> | null;
type Rej = { rejectValue: iStateMessage };

const mkThunk = <T = void>(name: string, fn: (arg: T) => Promise<any>) =>
  createAsyncThunk<R, T, Rej>(curriedGetThunkName(name), async (arg, { rejectWithValue }) => {
    try {
      const response = await fn(arg);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if ([httpStatusCodes.SUCCESS_OK, httpStatusCodes.SUCCESS_CREATED].includes(httpStatusCode)) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch { return rejectWithValue({ message: "Something went wrong" }); }
  });

export const getLeads = mkThunk<{ status?: string; source?: string; assigned_to?: string; search?: string; page?: number; limit?: number; date_from?: string; date_to?: string; tag_id?: string; form?: string; sort_by?: string; sort_dir?: string; view?: string; unassigned?: boolean } | undefined>(
  'getLeads', (p) => crmSetup.getLeads(p?.status, p?.source, p?.assigned_to, p?.search, p?.page, p?.limit, p?.date_from, p?.date_to, p?.tag_id, p?.form, p?.sort_by, p?.sort_dir, p?.view, p?.unassigned)
);

export const getLeadViewCounts = mkThunk<void>('getLeadViewCounts', () => crmSetup.getLeadViewCounts());

export const getLeadFormNames = mkThunk<void>('getLeadFormNames', () => crmSetup.getLeadFormNames());

export const getLeadSources = mkThunk<void>('getLeadSources', () => crmSetup.getLeadSources());

export const getLead = mkThunk<string>('getLead', (id) => crmSetup.getLead(id));

export const createLead = mkThunk<{ name: string; phone: string; email?: string; source?: string; notes?: string; assigned_to?: string }>(
  'createLead', (payload) => crmSetup.createLead(payload)
);

export const updateLead = mkThunk<{ id: string } & Record<string, any>>(
  'updateLead', ({ id, ...payload }) => crmSetup.updateLead(id, payload)
);

export const deleteLead = mkThunk<string>('deleteLead', (id) => crmSetup.deleteLead(id));

export const bulkImportLeads = mkThunk<File>('bulkImportLeads', (file) => crmSetup.bulkImportLeads(file));

export const assignLead = mkThunk<{ leadId: string; telecallerId: string }>(
  'assignLead', ({ leadId, telecallerId }) => crmSetup.assignLead(leadId, telecallerId)
);

export const getTelecallers = mkThunk<void>('getTelecallers', () => crmSetup.getTelecallers());

export const createTelecaller = mkThunk<{ name: string; phone?: string; email: string; daily_target?: number; password: string }>(
  'createTelecaller', (payload) => crmSetup.createTelecaller(payload)
);

export const getUnlinkedEmployees = mkThunk<void>('getUnlinkedEmployees', () => crmSetup.getUnlinkedEmployees());
export const linkEmployeeAsTelecaller = mkThunk<{ userId: string; daily_target?: number }>(
  'linkEmployeeAsTelecaller', (payload) => crmSetup.linkEmployeeAsTelecaller(payload)
);

export const updateTelecaller = mkThunk<{ id: string } & Record<string, any>>(
  'updateTelecaller', ({ id, ...payload }) => crmSetup.updateTelecaller(id, payload)
);

export const deleteTelecaller = mkThunk<string>('deleteTelecaller', (id) => crmSetup.deleteTelecaller(id));

export const logCall = mkThunk<{ leadId: string; telecaller_id: string; duration_seconds: number; status_after_call: string; notes: string; next_followup_at?: string }>(
  'logCall', ({ leadId, ...payload }) => crmSetup.logCall(leadId, payload)
);

export const uploadCallRecording = mkThunk<{ leadId: string; logId: string; file: File }>(
  'uploadCallRecording', ({ leadId, logId, file }) => crmSetup.uploadCallRecording(leadId, logId, file)
);

export const getCallLogs = mkThunk<string | undefined>('getCallLogs', (leadId) => crmSetup.getCallLogs(leadId));
export const getLeadTimeline = mkThunk<string>('getLeadTimeline', (leadId) => crmSetup.getLeadTimeline(leadId));

export const getAdminDashboard = mkThunk<{ date_from?: string; date_to?: string } | undefined>(
  'getAdminDashboard', (p) => crmSetup.getAdminDashboard(p?.date_from, p?.date_to)
);

export const getTelecallerDashboard = mkThunk<string>(
  'getTelecallerDashboard', (telecallerId) => crmSetup.getTelecallerDashboard(telecallerId)
);

export const getFollowups = mkThunk<{ telecaller_id?: string; overdue_only?: boolean } | undefined>(
  'getFollowups', (p) => crmSetup.getFollowups(p?.telecaller_id, p?.overdue_only)
);

export const completeFollowup = mkThunk<string>('completeFollowup', (id) => crmSetup.completeFollowup(id));

export const getDailyReport = mkThunk<string | undefined>('getDailyReport', (date) => crmSetup.getDailyReport(date));

export const getWeeklyReport = mkThunk<string | undefined>('getWeeklyReport', (week_start) => crmSetup.getWeeklyReport(week_start));

export const getPerformanceReport = mkThunk<{ date_from?: string; date_to?: string; telecaller_id?: string } | undefined>(
  'getPerformanceReport', (p) => crmSetup.getPerformanceReport(p)
);

export const getLeadScoreSummary = mkThunk<void>('getLeadScoreSummary', () => crmSetup.getLeadScoreSummary());

export const getWhatsAppTemplates = mkThunk<void>('getWhatsAppTemplates', () => crmSetup.getWhatsAppTemplates());

export const createWhatsAppTemplate = mkThunk<{ name?: string; source: string; trigger_event: string; template_text: string; is_default?: boolean }>(
  'createWhatsAppTemplate', (payload) => crmSetup.createWhatsAppTemplate(payload)
);
export const updateWhatsAppTemplate = mkThunk<{ id: string; name?: string; source?: string; trigger_event?: string; template_text?: string; is_default?: boolean }>(
  'updateWhatsAppTemplate', ({ id, ...payload }) => crmSetup.updateWhatsAppTemplate(id, payload)
);
export const deleteWhatsAppTemplate = mkThunk<string>('deleteWhatsAppTemplate', (id) => crmSetup.deleteWhatsAppTemplate(id));

export const markWhatsAppSent = mkThunk<string>('markWhatsAppSent', (leadId) => crmSetup.markWhatsAppSent(leadId));
export const sendLeadWhatsApp = mkThunk<{ leadId: string; message?: string; attachmentFile?: File | null }>(
  'sendLeadWhatsApp', ({ leadId, message, attachmentFile }) => crmSetup.sendLeadWhatsApp(leadId, message, attachmentFile)
);
export const draftLeadWhatsAppMessage = mkThunk<string>('draftLeadWhatsAppMessage', (leadId) => crmSetup.draftLeadWhatsAppMessage(leadId));
export const getWASessionStatus = mkThunk<void>('getWASessionStatus', () => crmSetup.getWASessionStatus());

export const getOverdueNotifications = mkThunk<void>('getOverdueNotifications', () => crmSetup.getOverdueNotifications());

export const assignLeadSafe = mkThunk<{ leadId: string; telecallerId: string }>(
  'assignLeadSafe', ({ leadId, telecallerId }) => crmSetup.assignLeadSafe(leadId, telecallerId)
);

export const exportDeadLeads = mkThunk<void>('exportDeadLeads', () => crmSetup.exportDeadLeads());

export const bulkSendWhatsApp = mkThunk<{ leadIds?: string[]; filters?: Record<string, any>; message: string; imageFile?: File | null; excludeOptOutText?: boolean; scheduledAt?: string }>(
  'bulkSendWhatsApp', (payload) => crmSetup.bulkSendWhatsApp(payload)
);
export const getWhatsAppBulkJobs = mkThunk<void>('getWhatsAppBulkJobs', () => crmSetup.getWhatsAppBulkJobs());
export const getWhatsAppBulkJobDetail = mkThunk<string>('getWhatsAppBulkJobDetail', (id) => crmSetup.getWhatsAppBulkJobDetail(id));
export const optOutLeadWhatsApp = mkThunk<string>('optOutLeadWhatsApp', (leadId) => crmSetup.optOutLeadWhatsApp(leadId));
export const optInLeadWhatsApp = mkThunk<string>('optInLeadWhatsApp', (leadId) => crmSetup.optInLeadWhatsApp(leadId));

export const syncFacebookLeads = mkThunk<void>('syncFacebookLeads', () => crmSetup.syncFacebook());

export const shuffleUnassignedLeads = mkThunk<void>('shuffleUnassignedLeads', () => crmSetup.shuffleUnassignedLeads());

export const backfillFormNames = mkThunk<void>('backfillFormNames', () => crmSetup.backfillFormNames());

export const getCrmApiKey = mkThunk<void>('getCrmApiKey', () => crmSetup.getApiKey());

export const getStatuses = mkThunk<void>('getStatuses', () => crmSetup.getStatuses());
export const createStatus = mkThunk<{ label: string; color?: string }>('createStatus', (payload) => crmSetup.createStatus(payload));
export const updateStatus = mkThunk<{ id: string } & Record<string, any>>('updateStatus', ({ id, ...payload }) => crmSetup.updateStatus(id, payload));
export const deleteStatus = mkThunk<string>('deleteStatus', (id) => crmSetup.deleteStatus(id));

export const getTags = mkThunk<void>('getTags', () => crmSetup.getTags());
export const createTag = mkThunk<{ name: string; color?: string }>('createTag', (payload) => crmSetup.createTag(payload));
export const updateTag = mkThunk<{ id: string } & Record<string, any>>('updateTag', ({ id, ...payload }) => crmSetup.updateTag(id, payload));
export const deleteTag = mkThunk<string>('deleteTag', (id) => crmSetup.deleteTag(id));

export const setLeadTags = mkThunk<{ leadId: string; tagIds: string[] }>('setLeadTags', ({ leadId, tagIds }) => crmSetup.setLeadTags(leadId, tagIds));
